import { useMemo, useState } from 'react'
import { faBank } from '@fortawesome/pro-regular-svg-icons'
import { institutionLogoMap } from '@/src/constants/logoConstants'
import { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import { Icon, Image, Skeleton } from '@/ui'

/** Case-insensitive index of the logo map, so free-text names (e.g. a manual account's
 * typed "chase") resolve regardless of casing/surrounding whitespace. */
const logoByNormalizedName = new Map(
  Object.entries(institutionLogoMap).map(([name, logo]) => [
    name.toLowerCase(),
    logo,
  ])
)

const resolveLogoByName = (name: string | undefined): string | undefined =>
  name ? logoByNormalizedName.get(name.trim().toLowerCase()) : undefined

/** Plaid ships the logo as base64; the static map covers institutions it does not. */
export const resolveInstitutionLogo = (
  institution?: PlaidItem,
  institutionName?: string
): string | undefined => {
  if (institution?.logo) return `data:image/png;base64, ${institution.logo}`

  return resolveLogoByName(institution?.institutionName ?? institutionName)
}

interface AccountInstitutionLogoProps {
  institution?: PlaidItem
  /** Fallback institution name (e.g. a manual account's typed name) used to resolve a
   * logo from the static map when there is no linked PlaidItem. */
  institutionName?: string
}

export const AccountInstitutionLogo = ({
  institution,
  institutionName,
}: AccountInstitutionLogoProps) => {
  const [loaded, setLoaded] = useState(false)

  const name = institution?.institutionName ?? institutionName

  const src = useMemo(() => {
    if (institution?.logo) return `data:image/png;base64, ${institution.logo}`
    return resolveLogoByName(name)
  }, [institution, name])

  if (src) {
    return (
      <div className="size-12 shrink-0">
        {!loaded && <Skeleton className="size-full rounded-lg" />}
        <Image
          src={src}
          alt={`${name} logo`}
          width={48}
          height={48}
          loading="eager"
          className={loaded ? '' : 'invisible absolute'}
          onLoad={() => setLoaded(true)}
        />
      </div>
    )
  }

  return (
    <div className="size-12 shrink-0 flex items-center justify-center rounded-lg bg-neutral-100">
      <Icon icon={faBank} color="gray" />
    </div>
  )
}
